"use client";

import { quoteThemeColorStyles, quoteThemeCssVars } from "../quote-theme/quote-theme-css-vars";
import { Check, Info } from "lucide-react";
import { useRef, useEffect, useState } from "react";

import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { iInclusionBlockData } from "@/@sdk/holiday-quotes-x/holiday-quotes-x-model";

interface InclusionsSectionProps extends iInclusionBlockData {
  primaryColor?: string; // legacy, theme CSS vars used
}

export function InclusionsSection({
  inclusions,
  sectionTitle = "What's Included",
  }: InclusionsSectionProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isWide, setIsWide] = useState(false);

  useEffect(() => {
    const checkWidth = () => {
      if (containerRef.current) {
        const width = containerRef.current.offsetWidth;
        setIsWide(width >= 768); // 768px breakpoint
      }
    };

    checkWidth();
    window.addEventListener('resize', checkWidth);
    
    // Use ResizeObserver for more accurate container size detection
    const resizeObserver = new ResizeObserver(checkWidth);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      window.removeEventListener('resize', checkWidth);
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <section className="bg-white rounded-3xl shadow-sm p-4 sm:p-6">
      <h2 className="text-xl sm:text-2xl font-semibold mb-4 sm:mb-6">{sectionTitle}</h2>

      <div 
        ref={containerRef}
        className={`grid gap-4 sm:gap-8 ${isWide ? 'grid-cols-2' : 'grid-cols-1'}`}
      >
        {inclusions.map((category) => (
            <div key={category.id} className="space-y-3 sm:space-y-4">
              <h3 className="text-base sm:text-lg font-medium text-gray-900">
                {category.category}
              </h3>

              <div className="space-y-2 sm:space-y-3">
                {category.items.map((item) => (
                  <div
                    key={item.id}
                    className="flex items-start gap-2 sm:gap-3 p-2 sm:p-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors"
                  >
                    <div
                      className={`flex-shrink-0 w-4 h-4 sm:w-5 sm:h-5 rounded-full flex items-center justify-center`}
                      style={{
                        backgroundColor: item.included
                          ? quoteThemeCssVars.primary10
                          : "rgb(243 244 246)",
                        color: item.included ? quoteThemeCssVars.primary : "rgb(156 163 175)",
                      }}
                    >
                      <Check className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                    </div>

                    <div className="flex-grow">
                      <div className="flex items-center gap-1.5 sm:gap-2">
                        <span
                          className={`text-sm ${
                            item.included
                              ? "text-gray-900"
                              : "text-gray-500 line-through"
                          }`}
                        >
                          {item.text}
                        </span>

                        {item.details && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Info
                                className="w-3.5 h-3.5 sm:w-4 sm:h-4 cursor-help flex-shrink-0"
                                style={quoteThemeColorStyles.primary}
                              />
                            </TooltipTrigger>
                            <TooltipContent>
                              {item.details}
                            </TooltipContent>
                          </Tooltip>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
    </section>
  );
}
